import { useCallback, useEffect, useMemo, useState } from "react";
import type { ChallengeListResponse } from "@versedrill/core";
import { GameView } from "@/components/GameView.tsx";
import { ResultPanel } from "@/components/ResultPanel.tsx";
import { api, dailySource } from "@/lib/api.ts";
import { useStore } from "@/lib/store.ts";

/** Newest playable game the player hasn't finished, other than `exclude`. */
export function newestUnplayed(list: ChallengeListResponse | null, stars: Record<number, number>, exclude?: number): number | null {
  if (!list) return null;
  return list.challenges.find((c) => stars[c.number] === undefined && c.number !== exclude)?.number ?? null;
}

/** A daily game (`/challenges/:n`, or whatever "/" resolves to). */
export function GamePage({ number }: { number: number }) {
  const source = useMemo(() => dailySource(number), [number]);
  const stars = useStore((s) => s.stars);
  const recordResult = useStore((s) => s.recordResult);
  const [list, setList] = useState<ChallengeListResponse | null>(null);

  useEffect(() => {
    api.list().then(setList).catch(() => setList(null));
  }, [number]);
  const nextNumber = useMemo(() => newestUnplayed(list, stars, number), [list, stars, number]);
  const onFinished = useCallback((r: { stars: number }) => recordResult(number, r.stars), [recordResult, number]);

  return (
    <GameView
      source={source}
      onFinished={onFinished}
      previousStars={stars[number]}
      // The share is built from `challenge.text`, which is always redacted. The on-screen text (`shownText`) is real once solved.
      renderResult={({ result, challenge }) => <ResultPanel number={number} result={result} redactedText={challenge.text} nextNumber={nextNumber} />}
    />
  );
}
